import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import dayjs, { Dayjs } from 'dayjs';
import {
  AlertSeverity,
  ClientOption,
  CodeDescriptionOption,
  CodeNameOption,
  DownloadRequest,
  NameOption,
  setAlert,
  useAppDispatch,
  useGetFilterOptionsQuery,
} from 'api';
import { useDownloadValidation } from '../hooks/useDownloadValidation';

export const DownloadFilters = () => {
  const dispatch = useAppDispatch();
  const {
    data: options,
    isLoading: optionsLoading,
    isError: optionsError,
  } = useGetFilterOptionsQuery();

  // Filter selections. Lookups are multi-select (sent as string[], IN-matched).
  // `clients` narrows to a subset of the caller's own authorized clients; empty
  // = all of them (server default).
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [providerTypes, setProviderTypes] = useState<NameOption[]>([]);
  const [locations, setLocations] = useState<NameOption[]>([]);
  const [payerTypes, setPayerTypes] = useState<NameOption[]>([]);
  const [companies, setCompanies] = useState<NameOption[]>([]);
  const [pmSystems, setPmSystems] = useState<CodeNameOption[]>([]);
  const [chargeCodes, setChargeCodes] = useState<CodeDescriptionOption[]>([]);

  const datesValid = useMemo(
    () =>
      !!startDate &&
      !!endDate &&
      startDate.isValid() &&
      endDate.isValid() &&
      !startDate.isAfter(endDate),
    [startDate, endDate]
  );

  // Build the request, sending arrays for multi-select dims and null (not [])
  // when nothing is picked so the server treats it as "no filter" / all clients.
  const request: DownloadRequest = useMemo(
    () => ({
      startDate: startDate?.isValid() ? startDate.format('YYYY-MM-DD') : '',
      endDate: endDate?.isValid() ? endDate.format('YYYY-MM-DD') : '',
      clientKeys: clients.length ? clients.map(c => c.key) : null,
      providerType: providerTypes.length ? providerTypes.map(o => o.name) : null,
      location: locations.length ? locations.map(o => o.name) : null,
      payerType: payerTypes.length ? payerTypes.map(o => o.name) : null,
      company: companies.length ? companies.map(o => o.name) : null,
      pmSystem: pmSystems.length ? pmSystems.map(o => o.code) : null,
      chargeCode: chargeCodes.length ? chargeCodes.map(o => o.code) : null,
    }),
    [startDate, endDate, clients, providerTypes, locations, payerTypes, companies, pmSystems, chargeCodes]
  );

  const { phase, result, error, validate, submit, reset, isValidating, isCreating } =
    useDownloadValidation(request);

  // On a successful submission, toast and return the footer to its initial
  // state; the history table refreshes itself via cache invalidation.
  useEffect(() => {
    if (phase === 'done') {
      dispatch(
        setAlert({
          message: 'Download started. Track its progress in the table below.',
          severity: AlertSeverity.Success,
        })
      );
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const readyToSubmit = phase === 'validated' && result?.valid === true;

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        Filters
      </Typography>

      {optionsError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Filter options could not be loaded. You can still download by date
          range, or try reloading the page.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        }}
      >
        <DatePicker
          label="Start date *"
          value={startDate}
          onChange={setStartDate}
          maxDate={endDate ?? undefined}
          slotProps={{ field: { clearable: true } }}
        />
        <DatePicker
          label="End date *"
          value={endDate}
          onChange={setEndDate}
          minDate={startDate ?? undefined}
          slotProps={{ field: { clearable: true } }}
        />

        <Autocomplete
          multiple
          disableCloseOnSelect
          limitTags={2}
          options={options?.clients ?? []}
          getOptionLabel={o => o.name}
          isOptionEqualToValue={(o, v) => o.key === v.key}
          value={clients}
          onChange={(_, v) => setClients(v)}
          loading={optionsLoading}
          renderInput={params => (
            <TextField
              {...params}
              label="Client"
              placeholder={clients.length ? undefined : 'All my clients'}
            />
          )}
        />
        <Autocomplete
          multiple
          disableCloseOnSelect
          limitTags={2}
          options={options?.providerTypes ?? []}
          getOptionLabel={o => o.name}
          isOptionEqualToValue={(o, v) => o.name === v.name}
          value={providerTypes}
          onChange={(_, v) => setProviderTypes(v)}
          loading={optionsLoading}
          renderInput={params => (
            <TextField {...params} label="Provider type" />
          )}
        />
        <Autocomplete
          multiple
          disableCloseOnSelect
          limitTags={2}
          options={options?.locations ?? []}
          getOptionLabel={o => o.name}
          isOptionEqualToValue={(o, v) => o.name === v.name}
          value={locations}
          onChange={(_, v) => setLocations(v)}
          loading={optionsLoading}
          renderInput={params => <TextField {...params} label="Location" />}
        />
        <Autocomplete
          multiple
          disableCloseOnSelect
          limitTags={2}
          options={options?.payerTypes ?? []}
          getOptionLabel={o => o.name}
          isOptionEqualToValue={(o, v) => o.name === v.name}
          value={payerTypes}
          onChange={(_, v) => setPayerTypes(v)}
          loading={optionsLoading}
          renderInput={params => <TextField {...params} label="Payer type" />}
        />
        <Autocomplete
          multiple
          disableCloseOnSelect
          limitTags={2}
          options={options?.companies ?? []}
          getOptionLabel={o => o.name}
          isOptionEqualToValue={(o, v) => o.name === v.name}
          value={companies}
          onChange={(_, v) => setCompanies(v)}
          loading={optionsLoading}
          renderInput={params => <TextField {...params} label="Company" />}
        />
        <Autocomplete
          multiple
          disableCloseOnSelect
          limitTags={2}
          options={options?.pmSystems ?? []}
          getOptionLabel={o => o.name}
          isOptionEqualToValue={(o, v) => o.code === v.code}
          value={pmSystems}
          onChange={(_, v) => setPmSystems(v)}
          loading={optionsLoading}
          renderInput={params => <TextField {...params} label="PM system" />}
        />
        <Autocomplete
          multiple
          disableCloseOnSelect
          limitTags={2}
          options={options?.ooChargeCodes ?? []}
          getOptionLabel={o =>
            o.description ? `${o.code} — ${o.description}` : o.code
          }
          isOptionEqualToValue={(o, v) => o.code === v.code}
          value={chargeCodes}
          onChange={(_, v) => setChargeCodes(v)}
          loading={optionsLoading}
          renderInput={params => <TextField {...params} label="Charge code" />}
        />
      </Box>

      <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {phase === 'validated' && result && !result.valid && (
          <Alert severity="warning">{result.message}</Alert>
        )}
        {readyToSubmit && result && (
          <Alert severity="success">
            This query returns {result.count.toLocaleString()} row
            {result.count === 1 ? '' : 's'}. Review your filters, then start the
            download.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {!readyToSubmit ? (
            <Button
              variant="contained"
              onClick={validate}
              loading={isValidating}
              loadingPosition="start"
              startIcon={<PlaylistAddCheckIcon />}
              disabled={!datesValid || isValidating}
            >
              Validate query
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                onClick={submit}
                loading={isCreating}
                loadingPosition="start"
                startIcon={<FileDownloadOutlinedIcon />}
                disabled={isCreating}
              >
                Start download
              </Button>
              <Button variant="text" onClick={reset} disabled={isCreating}>
                Change filters
              </Button>
            </>
          )}
          {!datesValid && (
            <Typography variant="caption" color="text.secondary">
              Select a valid start and end date to continue.
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
